import { useEffect, useRef, useState } from "react";
import { Play, Pause, Minus, Plus } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { getCtx, clickMetronome } from "@/lib/audio";

const SIGNATURES = [
    { id: "2/4", beats: 2 },
    { id: "3/4", beats: 3 },
    { id: "4/4", beats: 4 },
    { id: "6/8", beats: 6 },
    { id: "7/8", beats: 7 },
    { id: "5/4", beats: 5 },
];

export default function Metronome() {
    const [bpm, setBpm] = useState(100);
    const [sig, setSig] = useState("4/4");
    const [playing, setPlaying] = useState(false);
    const [beat, setBeat] = useState(-1);
    const sched = useRef({ nextTime: 0, beat: 0, intervalId: null });

    const beats = SIGNATURES.find((s) => s.id === sig).beats;

    useEffect(() => {
        if (!playing) {
            if (sched.current.intervalId) clearInterval(sched.current.intervalId);
            sched.current.intervalId = null;
            setBeat(-1);
            return;
        }
        const ctx = getCtx();
        const spb = 60 / bpm;
        sched.current.nextTime = ctx.currentTime + 0.05;
        sched.current.beat = 0;
        const scheduler = () => {
            const lookahead = ctx.currentTime + 0.25;
            while (sched.current.nextTime < lookahead) {
                const idx = sched.current.beat % beats;
                clickMetronome(sched.current.nextTime, idx === 0);
                const at = (sched.current.nextTime - ctx.currentTime) * 1000;
                setTimeout(() => setBeat(idx), Math.max(0, at));
                sched.current.beat += 1;
                sched.current.nextTime += spb;
            }
        };
        scheduler();
        const id = setInterval(scheduler, 80);
        sched.current.intervalId = id;
        return () => clearInterval(id);
    }, [playing, bpm, beats]);

    return (
        <div data-testid="metronome-page" className="space-y-10">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Tool · 03
                </div>
                <h1 className="font-sans font-extrabold text-5xl lg:text-6xl tracking-tight">
                    Metronome
                </h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    A focused metronome with adjustable tempo and time signature. The first beat of every bar is accented.
                </p>
            </header>

            {/* Visual pulse + BPM */}
            <div className="rounded-lg border border-border bg-card p-10 grid lg:grid-cols-2 gap-10 items-center">
                <div className="relative h-[260px] grid place-items-center schematic rounded-md">
                    <div
                        data-testid="metronome-pulse"
                        className={`w-32 h-32 rounded-full border-2 transition-all duration-100 ${
                            playing && beat === 0
                                ? "bg-primary border-primary scale-110"
                                : playing && beat > -1
                                  ? "bg-accent/30 border-accent scale-105"
                                  : "border-border scale-100"
                        }`}
                    />
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
                        Tempo
                    </div>
                    <div className="flex items-baseline gap-3">
                        <span
                            data-testid="metronome-bpm-value"
                            className="font-mono font-bold text-7xl sm:text-8xl tracking-tighter"
                        >
                            {bpm}
                        </span>
                        <span className="font-mono text-sm text-muted-foreground">
                            BPM
                        </span>
                    </div>
                    <div className="flex gap-2 mt-6">
                        <button
                            data-testid="metronome-bpm-minus"
                            onClick={() => setBpm((b) => Math.max(40, b - 1))}
                            className="w-10 h-10 grid place-items-center rounded-md border border-border hover:border-primary hover:text-primary"
                        >
                            <Minus size={16} />
                        </button>
                        <button
                            data-testid="metronome-bpm-plus"
                            onClick={() => setBpm((b) => Math.min(240, b + 1))}
                            className="w-10 h-10 grid place-items-center rounded-md border border-border hover:border-primary hover:text-primary"
                        >
                            <Plus size={16} />
                        </button>
                        <button
                            data-testid="metronome-play-button"
                            onClick={() => setPlaying((p) => !p)}
                            className={`ml-2 px-6 h-10 rounded-md font-medium text-sm flex items-center gap-2 ${
                                playing
                                    ? "bg-foreground text-background"
                                    : "bg-primary text-primary-foreground"
                            }`}
                        >
                            {playing ? (
                                <>
                                    <Pause size={16} /> Stop
                                </>
                            ) : (
                                <>
                                    <Play size={16} /> Start
                                </>
                            )}
                        </button>
                    </div>

                    <div className="mt-8">
                        <Slider
                            data-testid="metronome-bpm-slider"
                            value={[bpm]}
                            onValueChange={([v]) => setBpm(v)}
                            min={40}
                            max={240}
                            step={1}
                        />
                    </div>
                </div>
            </div>

            {/* Time signature + beats */}
            <div className="grid sm:grid-cols-2 gap-6">
                <div className="rounded-lg border border-border bg-card p-6">
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">
                        Time signature
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SIGNATURES.map((s) => (
                            <button
                                key={s.id}
                                data-testid={`metronome-sig-${s.id.replace("/", "-")}`}
                                onClick={() => setSig(s.id)}
                                className={`px-4 h-10 rounded-md border text-sm font-medium font-mono transition ${
                                    s.id === sig
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-secondary/40 border-border text-foreground hover:border-primary/50"
                                }`}
                            >
                                {s.id}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="rounded-lg border border-border bg-card p-6">
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">
                        Beats in this bar
                    </div>
                    <div className="flex gap-2">
                        {Array.from({ length: beats }).map((_, i) => (
                            <div
                                key={i}
                                data-testid={`metronome-beat-${i}`}
                                className={`flex-1 h-12 rounded-md border flex items-center justify-center font-mono text-sm transition-all ${
                                    i === beat
                                        ? i === 0
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-accent/20 border-accent text-accent"
                                        : "border-border text-muted-foreground"
                                }`}
                            >
                                {i + 1}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
